import { useCallback, useRef, useState } from 'react'
import { createWidget, deleteWidget, Widget } from '../services/api'

// Widget types that require a DB row to exist before the user can interact
// with the rest of the form (e.g. banner_image needs a widget_id for image
// uploads). Extend here as new widget types join this club.
const AUTO_CREATE_TYPES = new Set<string>(['banner_image', 'hero_banner'])

export interface WidgetFormState {
  // Controlled values
  name: string
  setName: (v: string) => void
  type: string
  setType: (v: string) => void
  metadata: Record<string, unknown>
  setMetadata: (v: Record<string, unknown>) => void

  // The widget's DB id, once one exists. Populated from initialWidget on
  // edit, or when auto-create succeeds on a new banner_image widget.
  widgetId: string | null

  // True iff the widget was born in this session (via auto-create). Used to
  // decide whether a cancel should DELETE the row.
  autoCreated: boolean

  // True while the auto-create POST is in flight.
  autoCreating: boolean

  // True when the underlying widget should be treated as locked — initial
  // edit mode, or the widget has been auto-created and committed.
  typeLocked: boolean

  // Wire to the Name input's onBlur. Triggers auto-create when appropriate.
  handleNameBlur: () => void

  // Call when the form is closed without an explicit save. Deletes the
  // widget if we auto-created it this session.
  discardIfAutoCreated: () => void

  // Call after an explicit save (widget and/or placement) completes, so the
  // auto-created widget is no longer treated as a draft.
  markCommitted: () => void

  // Reset to a new initial state (e.g. drawer reopen, tab switch).
  reset: (initialWidget?: Widget | null) => void
}

interface Options {
  siteId: string
  initialWidget?: Widget | null
  // Called after auto-create or discard so the parent can refresh its
  // widget list (typically an SWR mutate).
  onWidgetsChanged?: () => void | Promise<void>
}

export function useWidgetFormState({ siteId, initialWidget, onWidgetsChanged }: Options): WidgetFormState {
  const [widget, setWidget] = useState<Widget | null>(initialWidget ?? null)
  const [name, setName] = useState(initialWidget?.name ?? '')
  const [type, setType] = useState(initialWidget?.widget_type ?? '')
  const [metadata, setMetadata] = useState<Record<string, unknown>>(initialWidget?.metadata ?? {})
  const [autoCreated, setAutoCreated] = useState(false)
  const [autoCreating, setAutoCreating] = useState(false)

  // One-shot guard — don't keep trying to auto-create every time the name
  // field blurs. Reset on reset().
  const autoCreateAttemptedRef = useRef(false)
  // Closed-while-in-flight safety — if the user cancels during the POST,
  // clean up the widget that the server just created.
  const closedRef = useRef(false)

  const fireOnChange = useCallback(async () => {
    if (onWidgetsChanged) await onWidgetsChanged()
  }, [onWidgetsChanged])

  const handleNameBlur = useCallback(async () => {
    if (autoCreateAttemptedRef.current) return
    if (widget) return
    if (!AUTO_CREATE_TYPES.has(type)) return
    if (!name.trim()) return

    autoCreateAttemptedRef.current = true
    setAutoCreating(true)
    try {
      const created = await createWidget({
        site_id: siteId,
        widget_type: type,
        name: name.trim(),
        metadata,
      })
      if (closedRef.current) {
        void deleteWidget(created.id).catch(() => {})
        return
      }
      setWidget(created)
      setAutoCreated(true)
      await fireOnChange()
    } catch (err) {
      autoCreateAttemptedRef.current = false
      // Re-throw so the caller can surface a toast if desired.
      throw err
    } finally {
      setAutoCreating(false)
    }
  }, [name, type, metadata, siteId, widget, fireOnChange])

  const discardIfAutoCreated = useCallback(() => {
    closedRef.current = true
    if (autoCreated && widget) {
      // Fire-and-forget — caller is already navigating away.
      void deleteWidget(widget.id).then(() => fireOnChange()).catch(() => {})
    }
  }, [autoCreated, widget, fireOnChange])

  const markCommitted = useCallback(() => {
    setAutoCreated(false)
  }, [])

  const reset = useCallback((next?: Widget | null) => {
    closedRef.current = false
    autoCreateAttemptedRef.current = false
    setAutoCreated(false)
    setAutoCreating(false)
    setWidget(next ?? null)
    setName(next?.name ?? '')
    setType(next?.widget_type ?? '')
    setMetadata((next?.metadata ?? {}) as Record<string, unknown>)
  }, [])

  return {
    name, setName,
    type, setType,
    metadata, setMetadata,
    widgetId: widget?.id ?? null,
    autoCreated,
    autoCreating,
    typeLocked: widget != null,
    handleNameBlur,
    discardIfAutoCreated,
    markCommitted,
    reset,
  }
}
